// skills/gatherer/gatherer.js
// Gatherer skill logic

import { GATHERING_ZONES, GATHERING_TOOLS, TIER_DROPS, FOOD_YIELDS } from './gathererData.js';
import { gameState } from '../../game/gameState.js';
import { addXP } from '../../systems/experience.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { showItemGain } from '../../ui/floatingNotifications.js';
import { pilferingSkill } from '../pilfering/pilfering.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { getTalentFlatBonus, getTalentLootBonus, getTalentProcChance, rollTalentProc, getGlobalRareLootBonus, incrementStackCount, resetStackCount, getStackingSpeedConfig, getStackCount } from '../../systems/talentTree/talentTree.js';
import { isBloomholdEligible } from '../../systems/bloomhold.js';
import { CROP_SEEDS, TREE_SEEDS } from '../../data/items/seeds.js';
import { bountySkill } from '../bounty/bounty.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { BaseSkill } from '../../core/BaseSkill.js';

class GathererSkill extends BaseSkill {
    constructor() {
        super(SKILLS.GATHERER, GATHERING_TOOLS, 'WORN_SATCHEL');
        // Track last zone for Zone Mastery stacking
        this.lastZoneId = null;
    }

    /**
     * Get the currently equipped tool object (alias for BaseSkill method)
     */
    get currentTool() {
        return this.getCurrentTool();
    }

    /**
     * Get the speed multiplier from highest owned tool
     */
    getSpeedMultiplier() {
        return this.getEffectiveSpeedMultiplier();
    }

    /**
     * Check if player can gather in a zone
     */
    canGather(zoneId) {
        // Find zone by id property
        const zone = Object.values(GATHERING_ZONES).find(z => z.id === zoneId);
        if (!zone) return { success: false, reason: 'Zone not found' };

        const skill = gameState.getSkill(SKILLS.GATHERER);
        if (skill.level < zone.level) {
            return { success: false, reason: `Requires level ${zone.level} Gatherer` };
        }

        // Check if primary drop (food) can fit in inventory
        const tierDrops = TIER_DROPS[zone.tier];
        if (!this.canFitItem(tierDrops.food)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    /**
     * Check if an item can fit in inventory (has empty slot OR existing stack with room)
     */
    canFitItem(itemId) {
        // Check if item exists in inventory with room to stack
        const existingSlot = gameState.findInventorySlot(itemId);
        if (existingSlot !== -1) {
            const slot = gameState.player.inventorySlots[existingSlot];
            if (slot.quantity < 999999999) { // MAX_STACK
                return true;
            }
        }
        // Check if there's an empty slot
        return gameState.hasInventorySpace();
    }

    /**
     * Start gathering in a zone
     */
    startGathering(zoneId) {
        // Find zone by id property
        const zone = Object.values(GATHERING_ZONES).find(z => z.id === zoneId);
        const canGather = this.canGather(zoneId);

        if (!canGather.success) {
            logger.debug('Cannot gather:', canGather.reason);
            return false;
        }

        // Calculate gathering time based on zone and highest owned tool
        const baseTime = zone.gatherTime;
        const speedMultiplier = this.getSpeedMultiplier();
        const gatherTime = baseTime / speedMultiplier;

        // Create gathering action
        const action = {
            type: 'gatherer',
            name: `Gathering in ${zone.name}`,
            zone: zone,
            zoneId: zoneId.toUpperCase(),
            progress: 0,
            duration: gatherTime,
            startTime: Date.now(),
            forceComplete: false,

            // Tick function called every game tick
            tick: (speedMultiplier = 1) => {
                // Check for force complete (used by admin panel)
                if (action.forceComplete) {
                    action.forceComplete = false;
                    const result = this.completeGathering(zone);
                    if (result.inventoryFull) {
                        toastWarning('Inventory full - gathering stopped');
                        gameState.clearAction();
                        return;
                    }
                    const canContinue = this.canGather(zoneId);
                    if (canContinue.success) {
                        this.startGathering(zoneId);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                // Apply speed multiplier to elapsed time
                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                // Complete when progress reaches 1
                if (action.progress >= 1) {
                    const result = this.completeGathering(zone);
                    if (result.inventoryFull) {
                        toastWarning('Inventory full - gathering stopped');
                        gameState.clearAction();
                        return;
                    }
                    const canContinue = this.canGather(zoneId);
                    if (canContinue.success) {
                        this.startGathering(zoneId);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    /**
     * Select a random item from a weighted pool
     * @param {Array} items - Array of { itemId, weight } objects
     * @returns {string} The selected itemId
     */
    selectWeightedItem(items) {
        const totalWeight = items.reduce((sum, item) => sum + item.weight, 0);
        let random = Math.random() * totalWeight;

        for (const item of items) {
            random -= item.weight;
            if (random <= 0) {
                return item.itemId;
            }
        }
        // Fallback to last item (shouldn't happen)
        return items[items.length - 1].itemId;
    }

    /**
     * Calculate drops for a zone
     */
    getDropTable(zone) {
        const drops = [];
        const tierDrops = TIER_DROPS[zone.tier];
        const yieldConfig = FOOD_YIELDS[zone.foodYield];

        // Calculate food quantity
        const foodQuantity = Math.floor(
            Math.random() * (yieldConfig.max - yieldConfig.min + 1) + yieldConfig.min
        );

        // Always drop food
        drops.push({
            itemId: tierDrops.food,
            quantity: foodQuantity
        });

        // Chance for bonus lower tier foods
        if (tierDrops.bonusFood && tierDrops.bonusFood.length > 0) {
            for (const bonus of tierDrops.bonusFood) {
                if (Math.random() < bonus.chance) {
                    // Bonus food drops 1-2 quantity
                    const bonusQty = Math.floor(Math.random() * 2) + 1;
                    drops.push({
                        itemId: bonus.itemId,
                        quantity: bonusQty
                    });
                }
            }
        }

        // Chance for bonus materials (crops, herbs, cloth)
        if (tierDrops.bonusMaterials && tierDrops.bonusMaterials.items.length > 0) {
            // Apply Bountiful Harvest talent bonus to chance
            const bonusMaterialBonus = getTalentFlatBonus(SKILLS.GATHERER, 'bonusMaterialChance');
            // Apply Husbandry Forager's Friend bonus (+15% Gatherer bonus material)
            const husbandryGathererBonus = getTalentFlatBonus(SKILLS.HUSBANDRY, 'gathererBonusMaterial');
            const adjustedChance = tierDrops.bonusMaterials.chance + bonusMaterialBonus + husbandryGathererBonus;

            if (Math.random() < adjustedChance) {
                const selectedItem = this.selectWeightedItem(tierDrops.bonusMaterials.items);
                // Bonus materials drop 1-2 quantity
                const bonusQty = Math.floor(Math.random() * 2) + 1;
                drops.push({
                    itemId: selectedItem,
                    quantity: bonusQty
                });
            }
        }

        // Chance for rare drop (awakening materials)
        // Apply Treasure Nose global bonus from Husbandry
        const globalRareBonus = getGlobalRareLootBonus();
        // Apply Awakening Specialist additive bonus
        const awakeningBonus = getTalentFlatBonus(SKILLS.GATHERER, 'awakeningChanceBonus');
        const adjustedRareChance = zone.rareChance + globalRareBonus + awakeningBonus;
        if (Math.random() < adjustedRareChance) {
            drops.push({
                itemId: tierDrops.rare,
                quantity: 1
            });
        }

        return drops;
    }

    /**
     * Roll for talent proc effects
     * Returns object with proc results
     */
    rollGathererProcs() {
        const procs = {
            doubleFood: false,
            petAssist: false,
            // New 3-branch procs
            seedFind: false,
            hiddenCache: false,
            tenXDrops: false,
            materialTransmutation: false
        };

        // Master Explorer: 10x drops (5%) - highest priority
        if (rollTalentProc(SKILLS.GATHERER, 'tenXDrops')) {
            procs.tenXDrops = true;
        }
        // Double Food (5% for 2x) - only if not higher procs
        else if (rollTalentProc(SKILLS.GATHERER, 'doubleFood')) {
            procs.doubleFood = true;
        }

        // Pet Assist (20% doubles all + Husbandry XP) - requires any pet unlocked
        if (rollTalentProc(SKILLS.GATHERER, 'petAssist')) {
            const pets = gameState.getPets?.() || {};
            const hasAnyPet = Object.values(pets).some(pet => pet.unlocked);
            if (hasAnyPet) {
                procs.petAssist = true;
            }
        }

        // Seed Finder (3-6% for crop/herb seeds)
        if (rollTalentProc(SKILLS.GATHERER, 'seedFind')) {
            procs.seedFind = true;
        }

        // Hidden Cache (5% for bonus loot chest)
        if (rollTalentProc(SKILLS.GATHERER, 'hiddenCache')) {
            procs.hiddenCache = true;
        }

        // Material Transmutation (10% for bonus upgraded materials)
        if (rollTalentProc(SKILLS.GATHERER, 'materialTransmutation')) {
            procs.materialTransmutation = true;
        }

        return procs;
    }

    /**
     * Get a random crop or herb seed for Seed Finder talent
     */
    getRandomSeed() {
        // Combine crop seeds (excluding ancient_seed) and herb seeds
        const herbSeeds = [
            'greenleaf_seed', 'duskleaf_seed', 'ironbloom_seed', 'emberroot_seed',
            'sanctum_leaf_seed', 'sunpetal_seed', 'deepvine_seed', 'frostmint_seed',
            'stonebark_seed', 'voidroot_seed', 'soulwort_seed', 'gloomshade_seed', 'celestine_seed'
        ];
        // Filter crop seeds to only include ones that actually exist
        const validCropSeeds = CROP_SEEDS.map(s => s.id);
        const allSeeds = [...validCropSeeds, ...herbSeeds];
        return allSeeds[Math.floor(Math.random() * allSeeds.length)];
    }

    /**
     * Award a seed, respecting Nature's Harmony (auto-deposit to BloomHold)
     */
    awardSeed(seedId) {
        const naturesHarmony = getTalentFlatBonus(SKILLS.GATHERER, 'seedsToBloomHold');

        if (naturesHarmony > 0 && gameState.isBloomholdUnlocked() && isBloomholdEligible(seedId)) {
            const result = gameState.addBloomholdItem(seedId, 1);
            if (result.success) {
                logger.game(`Nature's Harmony: ${seedId.replace(/_/g, ' ')} sent to BloomHold!`);
                showItemGain(seedId, 1);
                return;
            }
        }
        // Add to inventory
        gameState.addItem(seedId, 1);
        bountySkill.trackItemProgress(seedId, 1);
        showItemGain(seedId, 1);
    }

    /**
     * Upgrade a herb to the next tier for Herb Attunement talent
     */
    upgradeHerbTier(herbId) {
        // Herb tier order (grimy herbs from gatherer)
        const herbTiers = [
            'grimy_greenleaf', 'grimy_duskleaf', 'grimy_ironbloom', 'grimy_emberroot',
            'grimy_sanctum_leaf', 'grimy_sunpetal', 'grimy_deepvine', 'grimy_frostmint',
            'grimy_stonebark', 'grimy_voidroot', 'grimy_soulwort', 'grimy_gloomshade', 'grimy_celestine'
        ];
        const currentIndex = herbTiers.indexOf(herbId);
        if (currentIndex >= 0 && currentIndex < herbTiers.length - 1) {
            return herbTiers[currentIndex + 1];
        }
        return herbId; // Already max tier or not found
    }

    /**
     * Upgrade a material to the next tier for Material Transmutation
     * Returns null if can't upgrade (already max tier or not upgradeable)
     */
    upgradeMaterialTier(itemId) {
        // Crop upgrade tiers
        const cropTiers = ['potato', 'onion', 'cabbage', 'tomato', 'corn', 'strawberry', 'watermelon', 'brightmaw', 'kings_fruit'];
        // Cloth upgrade tiers
        const clothTiers = ['linenweave', 'silkweave', 'aetherweave', 'runebound_cloth', 'shadowweave', 'voidcloth'];
        // Bone upgrade tiers
        const boneTiers = ['small_bone', 'charred_bone', 'ancient_bone', 'dragon_bone', 'primordial_bone'];

        // Check crops
        let idx = cropTiers.indexOf(itemId);
        if (idx >= 0 && idx < cropTiers.length - 1) return cropTiers[idx + 1];

        // Check cloth
        idx = clothTiers.indexOf(itemId);
        if (idx >= 0 && idx < clothTiers.length - 1) return clothTiers[idx + 1];

        // Check bones
        idx = boneTiers.indexOf(itemId);
        if (idx >= 0 && idx < boneTiers.length - 1) return boneTiers[idx + 1];

        return null; // Can't upgrade
    }

    /**
     * Generate Hidden Cache drops (mixed materials based on zone tier)
     */
    generateHiddenCacheDrops(zoneTier) {
        const drops = [];
        const tierDrops = TIER_DROPS[zoneTier];

        // Hidden cache contains 3-5 random items
        const numItems = Math.floor(Math.random() * 3) + 3;

        for (let i = 0; i < numItems; i++) {
            // 50% chance for bonus material, 30% food, 20% rare
            const roll = Math.random();
            if (roll < 0.5 && tierDrops.bonusMaterials?.items?.length > 0) {
                const item = this.selectWeightedItem(tierDrops.bonusMaterials.items);
                drops.push({ itemId: item, quantity: Math.floor(Math.random() * 3) + 1 });
            } else if (roll < 0.8) {
                drops.push({ itemId: tierDrops.food, quantity: Math.floor(Math.random() * 5) + 2 });
            } else {
                drops.push({ itemId: tierDrops.rare, quantity: 1 });
            }
        }

        return drops;
    }

    /**
     * Check if at max stacking speed for Gathering Momentum
     */
    isAtMaxStack() {
        const config = getStackingSpeedConfig(SKILLS.GATHERER);
        if (!config) return false;

        const currentStacks = getStackCount(SKILLS.GATHERER);
        const stackingCapBonus = getTalentFlatBonus(SKILLS.GATHERER, 'stackingCapBonus');
        const effectiveMaxStack = config.maxStack + stackingCapBonus;
        const maxStacks = Math.ceil(effectiveMaxStack / config.value);

        return currentStacks >= maxStacks;
    }

    /**
     * Complete a gathering action
     * @returns {{ inventoryFull: boolean }} Whether inventory became full
     */
    completeGathering(zone) {
        // Award XP (pass zone.id for admin node XP override)
        const xpResult = addXP(SKILLS.GATHERER, zone.xp, zone.id);

        // Increment Zone Mastery stacking speed
        incrementStackCount(SKILLS.GATHERER, zone.id);

        // Roll for talent procs
        const procs = this.rollGathererProcs();

        // Calculate drop multiplier from procs
        let dropMultiplier = 1;
        if (procs.tenXDrops) {
            dropMultiplier = 10;
        } else if (procs.doubleFood) {
            dropMultiplier = 2;
        }

        // Pet assist doubles everything on top of other procs
        if (procs.petAssist) {
            dropMultiplier *= 2;
            // Award Husbandry XP (scale with zone tier)
            const husbandryXP = Math.floor(zone.xp * 0.5);
            addXP(SKILLS.HUSBANDRY, husbandryXP, null, { skipCorruption: true });
        }

        // Apply Gathering Momentum bonus if at max stack
        const momentumBonus = getTalentFlatBonus(SKILLS.GATHERER, 'momentumYieldBonus');
        if (momentumBonus > 0 && this.isAtMaxStack()) {
            dropMultiplier *= (1 + momentumBonus);
            // Don't spam toast, just log
            logger.game('Gathering Momentum active! +25% yields');
        }

        // Apply Contract Momentum bonus (Bounty talent: +15% when bounty active for matching items)
        const tierDrops = TIER_DROPS[zone.tier];
        const contractMomentumBonus = bountySkill.getContractMomentumBonus(tierDrops?.food);
        if (contractMomentumBonus > 0) {
            dropMultiplier *= (1 + contractMomentumBonus);
        }

        // Calculate and add drops
        const drops = this.getDropTable(zone);
        let inventoryFull = false;

        // Apply loot bonus from talents to bonus materials
        const lootBonus = getTalentLootBonus(SKILLS.GATHERER);

        // Check for Bloom Touched talent (auto-deposit herbs to BloomHold)
        const hasBloomTouched = getTalentFlatBonus(SKILLS.ALCHEMY, 'bloomAutoDeposit') > 0;
        const bloomholdUnlocked = gameState.isBloomholdUnlocked();

        // Check for Herb Attunement talent
        const herbTierUpgrade = getTalentFlatBonus(SKILLS.GATHERER, 'herbTierUpgrade');

        for (const drop of drops) {
            let itemId = drop.itemId;
            // Apply drop multiplier
            let quantity = Math.floor(drop.quantity * dropMultiplier);

            // Apply loot bonus to non-food items (bonus materials)
            const isFood = itemId.includes('pet_food');
            if (!isFood && lootBonus > 0) {
                quantity = Math.floor(quantity * (1 + lootBonus));
            }

            // Check if this is a herb - apply Herb Attunement upgrade
            const isHerb = itemId.startsWith('grimy_');
            if (isHerb && herbTierUpgrade > 0) {
                itemId = this.upgradeHerbTier(itemId);
            }

            if (isHerb && hasBloomTouched && bloomholdUnlocked) {
                // Auto-deposit herb to BloomHold
                gameState.addBloomholdItem(itemId, quantity);
                showItemGain(itemId, quantity);
                logger.game(`Bloom Touched: ${quantity}x ${itemId} sent to BloomHold`);
            } else {
                const result = gameState.addItem(itemId, quantity);
                if (result.success && result.added > 0) {
                    showItemGain(itemId, result.added);
                    bountySkill.trackItemProgress(itemId, result.added);
                }
                // Track if any drop failed due to full inventory
                if (!result.success || result.added < quantity) {
                    inventoryFull = true;
                }

                // Material Transmutation - bonus upgraded material (not replacing)
                if (procs.materialTransmutation && !isFood && !isHerb) {
                    const upgradedItem = this.upgradeMaterialTier(itemId);
                    if (upgradedItem) {
                        const bonusQty = Math.max(1, Math.floor(quantity * 0.5)); // 50% of original as bonus
                        gameState.addItem(upgradedItem, bonusQty);
                        bountySkill.trackItemProgress(upgradedItem, bonusQty);
                        showItemGain(upgradedItem, bonusQty);
                    }
                }
            }
        }

        // Record drop multiplier proc notifications
        const foodItemId = tierDrops?.food;
        if (procs.tenXDrops && foodItemId) {
            recordTalentProc(SKILLS.GATHERER, 'Master Explorer', null, null, { itemId: foodItemId, quantity: 10 });
        } else if (procs.doubleFood && foodItemId) {
            recordTalentProc(SKILLS.GATHERER, 'Double Food', null, null, { itemId: foodItemId, quantity: 2 });
        }

        if (procs.petAssist) {
            recordTalentProc(SKILLS.GATHERER, 'Pet Assist', '2x all drops!', null);
        }

        if (procs.materialTransmutation) {
            recordTalentProc(SKILLS.GATHERER, 'Material Transmutation', 'Upgraded materials!', null);
        }

        // Seed Finder proc - drop random crop/herb seed
        if (procs.seedFind) {
            const seedId = this.getRandomSeed();
            this.awardSeed(seedId);
            recordTalentProc(SKILLS.GATHERER, 'Seed Finder', null, null, { itemId: seedId, quantity: 1 });
        }

        // Hidden Cache proc - bonus loot chest
        if (procs.hiddenCache) {
            const cacheDrops = this.generateHiddenCacheDrops(zone.tier);
            for (const cacheDrop of cacheDrops) {
                const result = gameState.addItem(cacheDrop.itemId, cacheDrop.quantity);
                if (result.success && result.added > 0) {
                    showItemGain(cacheDrop.itemId, result.added);
                    bountySkill.trackItemProgress(cacheDrop.itemId, result.added);
                }
            }
            recordTalentProc(SKILLS.GATHERER, 'Hidden Cache', 'Bonus loot found!', null);
        }

        // Check for pilferer contract assistance (rolls once per action completion)
        pilferingSkill.tryContractAssist('gatherer');

        // Notify UI of completion
        this.onGatheringComplete(zone, xpResult, drops);

        return { inventoryFull };
    }

    /**
     * Stop current gathering action
     */
    stopGathering() {
        gameState.clearAction();
    }

    /**
     * Purchase or equip a tool
     */
    upgradeTool(toolId) {
        const tool = Object.values(GATHERING_TOOLS).find(t => t.id === toolId);
        if (!tool) return { success: false, reason: 'Invalid tool' };

        const skill = gameState.getSkill(SKILLS.GATHERER);
        if (skill.level < tool.level) {
            return { success: false, reason: `Requires level ${tool.level} Gatherer` };
        }

        // Check if already owned
        if (gameState.hasPurchasedTool(SKILLS.GATHERER, toolId)) {
            // Just equip it
            gameState.equipTool(SKILLS.GATHERER, toolId);
            return { success: true, action: 'equipped' };
        }

        // Try to purchase
        const purchaseResult = gameState.purchaseTool(SKILLS.GATHERER, toolId, tool.price);
        if (!purchaseResult.success) {
            return purchaseResult;
        }

        // Auto-equip after purchase
        gameState.equipTool(SKILLS.GATHERER, toolId);
        return { success: true, action: 'purchased' };
    }

    /**
     * Get available zones for player's level
     */
    getAvailableZones() {
        const skill = gameState.getSkill(SKILLS.GATHERER);
        return Object.values(GATHERING_ZONES).filter(zone => zone.level <= skill.level);
    }

    /**
     * Callback for when gathering completes (override in UI)
     */
    onGatheringComplete(zone, xpResult, drops) {
        logger.game(`Gathered in ${zone.name}! +${zone.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Gatherer level up! Now level ${xpResult.newLevel}`);
        }
    }
}

// Singleton instance
export const gathererSkill = new GathererSkill();
export default gathererSkill;
